import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';
// Import Shadcn UI Components
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Link } from 'react-router-dom';
// Import Lucide Icons
import { Mail, Lock, Eye, EyeOff, Terminal, CheckCircle, Loader2, Building2, User } from 'lucide-react'; // Added Building2, User

export function SignUpForm() {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (!email || !fullName || !companyName || !password || !confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (companyName.trim().length < 2) {
      setError('Company name must be at least 2 characters long');
      return;
    }

    setIsLoading(true);

    try {
      // Sign up the user with additional metadata
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: {
            email: email,
            full_name: fullName,
            company_name: companyName, // Store company name in auth metadata
          }
        },
      });

      if (authError) throw authError;

      if (authData?.user) {
        setSuccessMessage(
          `Welcome to ResumeMatches! Please check your email for confirmation (Check your junk folder). ` +
          `Once confirmed, you'll be set up as the admin of ${companyName}.`
        );
      }
    } catch (err) {
      console.error('Signup error:', err);
      
      if (err instanceof Error) {
        const errorMessage = err.message.toLowerCase();
        if (errorMessage.includes('rate limit')) {
          setError('Too many signup attempts. Please try again later.');
        } else if (errorMessage.includes('already registered') || errorMessage.includes('already exists')) {
          setError('This email is already registered. Please sign in instead.');
        } else if (errorMessage.includes('invalid email')) {
          setError('Please enter a valid email address.');
        } else if (errorMessage.includes('weak password')) {
          setError('Please choose a stronger password.');
        } else {
          setError(`Signup failed: ${err.message}`);
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <div className="mx-auto h-12 w-12 bg-slate-900 rounded-xl flex items-center justify-center mb-4">
          <span className="text-white font-bold text-lg">RM</span>
        </div>
        <CardTitle className="text-2xl font-bold">Create Your Account</CardTitle>
        <CardDescription>Start your hiring journey and set up your organization.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Full Name Input */}
          <div className="space-y-1.5">
            <Label htmlFor="fullName">Full Name</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="fullName"
                type="text"
                placeholder="John Doe"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                disabled={isLoading || !!successMessage}
                className="pl-9"
              />
            </div>
          </div>

          {/* Email Input */}
          <div className="space-y-1.5">
            <Label htmlFor="email">Work Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading || !!successMessage}
                className="pl-9"
              />
             </div>
          </div>

          {/* Company Name Input */}
          <div className="space-y-1.5">
            <Label htmlFor="companyName">Company Name</Label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="companyName"
                type="text"
                placeholder="Your Company Inc."
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                required
                disabled={isLoading || !!successMessage}
                className="pl-9"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              You'll be set up as the admin of this organization
            </p>
          </div>

          {/* Password Input */}
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
               <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="password"
                type={showPasswords ? "text" : "password"}
                placeholder="•••••••• (min. 6 characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading || !!successMessage}
                className="pl-9 pr-10"
              />
               <Button 
                 type="button" variant="ghost" size="icon"
                 className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:text-foreground"
                 onClick={() => setShowPasswords(!showPasswords)}
                 disabled={isLoading || !!successMessage}
                 aria-label={showPasswords ? "Hide passwords" : "Show passwords"}
               >
                 {showPasswords ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
               </Button>
             </div>
          </div>
          
          {/* Confirm Password Input */}
          <div className="space-y-1.5">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <div className="relative">
               <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="confirmPassword"
                type={showPasswords ? "text" : "password"}
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={isLoading || !!successMessage}
                className="pl-9 pr-10"
              />
              {/* No toggle needed here, visibility linked to the one above */}
             </div>
          </div>

          {/* Terms Notice */}
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              By creating an account, you agree to our{' '}
              <Link to="/terms" className="font-medium text-primary hover:underline underline-offset-4">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy" className="font-medium text-primary hover:underline underline-offset-4">
                Privacy Policy
              </Link>
              .
            </p>
          </div>

           {/* Error Alert */}
          {error && (
             <Alert variant="destructive" className="py-2 px-3">
               <Terminal className="h-4 w-4" />
               {/* <AlertTitle>Error</AlertTitle> Optional Title */}
               <AlertDescription className="text-xs">{error}</AlertDescription>
             </Alert>
          )}

           {/* Success Alert */}
          {successMessage && (
             <Alert variant="default" className="py-3 px-4 bg-green-50 border-green-200 dark:bg-green-900/30 dark:border-green-700/50"> {/* Custom success style */}
               <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
               <AlertTitle className="text-sm font-medium text-green-700 dark:text-green-300">Account Created Successfully!</AlertTitle>
               <AlertDescription className="text-xs text-green-600 dark:text-green-400">
                  {successMessage}
               </AlertDescription>
             </Alert>
          )}

           {/* Submit Button */}
          <Button 
            type="submit" 
            disabled={isLoading || !!successMessage}
            className="w-full bg-slate-900 hover:bg-slate-800"
          >
            {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Create Account & Organization
          </Button>
        </form>
      </CardContent>
      <CardFooter className="flex flex-col items-center text-sm pt-4">
        <p className="text-muted-foreground">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-primary hover:underline underline-offset-4">
            Sign in
          </Link>
        </p>
      </CardFooter>
    </Card>
  );
}